import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert, Button, Form, Image, Input, InputNumber, Modal, Popconfirm, Select, Space, Table, Tabs, Tag, Typography, Upload, message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { UploadFile, UploadProps } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { del, get, patch, post } from '../../lib/request'
import request from '../../lib/request'
import { errMsg, fmtTime } from '../../lib/api'
import type { ProjectPageShareReward, RewardType, ShareReward } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { hasPermission } from '../../utils/permissions'
// merge_projects_into_projectintro_v1：「项目管理」并入本页作为一个标签页。
// 原样复用 Projects 页组件（不复制其编辑表单），避免出现两份项目编辑逻辑。
import ProjectsPage from '../Projects'

const { Text } = Typography

// ---- 海报大图规格（Contract Pack 几何常量，三层不得各写各的）----
const POSTER_IMG_W = 1027
const POSTER_IMG_H = 1365
const POSTER_UPLOAD_URL = '/admin/project-pages/poster-image/upload'
/** 提示文案逐字对齐设计稿 planning/_design_admin-poster-upload-panel.html */
// liuwei_pages_v12：项目介绍页顶部轮播横幅（bannerUrls，最多 5 张；后端 project-pages.ts::isValidBannerUrls 同上限）
const BANNER_MAX_COUNT = 5
const BANNER_MAX_BYTES = 500 * 1024
const BANNER_HINT = '项目介绍页顶部轮播横幅，最多 5 张；建议 1400×600 像素（7:3）JPG，单张不超过 500KB；比例不符时自动保留图片中间部分'
const POSTER_HINT = '仅支持 JPG / PNG，建议横图约 3:2，小程序居中裁切显示，单张 ≤ 10MB，可上传多张'
const POSTER_READONLY_HINT = '仅招商部超级管理员可上传或删除海报大图'

// ---- 实体类型（Contract Pack 拼写为准）----
export interface ProjectPageSection {
  title: string
  body: string
  images?: string[]
}

export type ProjectPageStatus = 'draft' | 'published' | 'offline'

export interface ProjectPageItem {
  id: number
  // multi_project_display_v1：所属项目（可空，挂到项目后首页卡片可补 category/项目简介）
  projectId?: number | null
  title: string
  titleEn?: string | null
  coverUrl?: string | null
  // 分享图（project_page_share_image_v2，可空）：转发给好友时微信卡片显示，空则回落封面图
  shareImageUrl?: string | null
  // 介绍长图（intro_long_image_v1，可空）：小程序介绍页全宽铺满展示的整图，独立于封面图与海报大图
  longImageUrl?: string | null
  // long_image_en_v1：介绍长图（英语版，可空）——空则小程序英文模式回落中文版长图
  longImageUrlEn?: string | null
  // 首页卡片简介（project_page_card_v1，可空）：首页项目卡片右下方简介，空则小程序回落项目简介
  cardDesc?: string | null
  cardDescEn?: string | null
  // lead_intent_category_v1：意向业态（office 写字楼/rental 租房/retail 商业；liuwei_v13 起新建/编辑必填，存量行可能为空）
  // 用户从该落地页留资时小程序自动携带进线索
  intentCategory?: string | null
  // 海报大图样式列表（visit_poster_v5，每项为图片 URL）
  posterImages?: string[] | null
  // landing_image_en_v1（ST2）：英语版封面图/分享图/海报图（可空，空则小程序英文模式回落中文版）
  coverUrlEn?: string | null
  shareImageUrlEn?: string | null
  posterImagesEn?: string[] | null
  // liuwei_pages_v12：项目介绍页顶部轮播横幅（可空，string[] 最多 5 张；NULL 视为无横幅）
  bannerUrls?: string[] | null
  // 分享答谢方案（share_reward_rule_end_v1，可空）：小程序分享弹窗展示的答谢方案内容
  // （有效期文字 + 区块列表 + 可选小卡片 + 底部注释；大标题固定走小程序 i18n，此处不录）
  shareReward?: ShareReward | null
  // share_reward_by_reward_type_v1（ST3）：按答谢类型分套的分享答谢方案子表行数组
  // （管理端 GET /admin/project-pages/:id/share-rewards 回填；编辑走 PUT .../share-rewards/:rewardTypeId）
  shareRewards?: ProjectPageShareReward[]
  sections: ProjectPageSection[]
  sortOrder: number
  status: ProjectPageStatus
  updatedBy?: number | null
  createdAt?: string
  updatedAt?: string
}

const STATUS_LABELS: Record<ProjectPageStatus, { label: string; color: string }> = {
  draft: { label: '草稿', color: 'default' },
  published: { label: '已上架', color: 'success' },
  offline: { label: '已下架', color: 'warning' },
}

interface PageFormValue {
  title: string
  titleEn?: string
  projectId?: number | null
  intentCategory?: string | null
  coverUrl?: string
  shareImageUrl?: string
  // landing_image_en_v1：英语版封面图/分享图（选填，空则英文模式回落中文版）
  coverUrlEn?: string
  shareImageUrlEn?: string
  longImageUrl?: string
  // long_image_en_v1：介绍长图（英语版 · 选填，空则英文模式回落中文版长图）
  longImageUrlEn?: string
  cardDesc?: string
  cardDescEn?: string
  sortOrder: number
  // 分享答谢方案（share_reward_rule_end_v1）扁平表单字段（Form.List 数组结构）：
  // title/titleEn 不出输入框恒为空串（大标题固定走小程序 i18n「本期答谢方案」）。
  shareRewardEffectiveText?: string
  shareRewardEffectiveTextEn?: string
  shareRewardNote?: string
  shareRewardNoteEn?: string
  shareRewardSections?: Array<{
    heading: string
    headingEn?: string
    body: string
    bodyEn?: string
    chips?: Array<{ label: string; labelEn?: string; value: string; valueEn?: string }>
  }>
}

/** 分享答谢方案编辑区上限（Contract Pack：区块 1..6、小卡片 0..4） */
const SHARE_REWARD_SECTION_MAX = 6
const SHARE_REWARD_CHIP_MAX = 4

/**
 * shareReward Json → 表单扁平字段（null/absent → undefined/空数组）。
 * 防御式解析：后端白名单保证形状，但历史脏数据不至于让弹窗崩溃。
 */
const shareRewardToFormFields = (sr: ShareReward | null | undefined) => {
  if (!sr || typeof sr !== 'object') {
    return {
      shareRewardEffectiveText: undefined,
      shareRewardEffectiveTextEn: undefined,
      shareRewardNote: undefined,
      shareRewardNoteEn: undefined,
      shareRewardSections: undefined,
    }
  }
  const sections = Array.isArray(sr.sections) ? sr.sections : []
  return {
    shareRewardEffectiveText: sr.effectiveText ?? undefined,
    shareRewardEffectiveTextEn: sr.effectiveTextEn ?? undefined,
    shareRewardNote: sr.note ?? undefined,
    shareRewardNoteEn: sr.noteEn ?? undefined,
    shareRewardSections: sections.length > 0
      ? sections.map((sec) => ({
          heading: sec.heading ?? '',
          headingEn: sec.headingEn ?? undefined,
          body: sec.body ?? '',
          bodyEn: sec.bodyEn ?? undefined,
          chips: Array.isArray(sec.chips)
            ? sec.chips.map((chip) => ({
                label: chip.label ?? '',
                labelEn: chip.labelEn ?? undefined,
                value: chip.value ?? '',
                valueEn: chip.valueEn ?? undefined,
              }))
            : undefined,
        }))
      : undefined,
  }
}

/**
 * 表单扁平字段 → shareReward 提交载荷（Contract Pack 拼写逐字）：
 * - 修剪首尾空白；空串可选键省略；空小卡片/空区块丢弃；
 * - title/titleEn 恒为空串（大标题走小程序 i18n，供图注释口径）；
 * - 全部内容为空（无有效期文字、无有效区块、无注释）→ 返回 null，允许清空配置。
 */
const assembleShareRewardPayload = (
  v: Pick<
    PageFormValue,
    | 'shareRewardEffectiveText'
    | 'shareRewardEffectiveTextEn'
    | 'shareRewardNote'
    | 'shareRewardNoteEn'
    | 'shareRewardSections'
  >,
): ShareReward | null => {
  const effectiveText = v.shareRewardEffectiveText?.trim() ?? ''
  const effectiveTextEn = v.shareRewardEffectiveTextEn?.trim() ?? ''
  const note = v.shareRewardNote?.trim() ?? ''
  const noteEn = v.shareRewardNoteEn?.trim() ?? ''
  const sections = (v.shareRewardSections ?? [])
    .map((sec) => {
      const heading = sec.heading?.trim() ?? ''
      const body = sec.body?.trim() ?? ''
      if (!heading && !body) return null
      const headingEn = sec.headingEn?.trim() ?? ''
      const bodyEn = sec.bodyEn?.trim() ?? ''
      const chips = (sec.chips ?? [])
        .map((chip) => {
          const label = chip.label?.trim() ?? ''
          const value = chip.value?.trim() ?? ''
          if (!label && !value) return null
          const labelEn = chip.labelEn?.trim() ?? ''
          const valueEn = chip.valueEn?.trim() ?? ''
          return {
            label,
            ...(labelEn ? { labelEn } : {}),
            value,
            ...(valueEn ? { valueEn } : {}),
          }
        })
        .filter((chip): chip is NonNullable<typeof chip> => chip !== null)
      return {
        heading,
        ...(headingEn ? { headingEn } : {}),
        body,
        ...(bodyEn ? { bodyEn } : {}),
        ...(chips.length > 0 ? { chips } : {}),
      }
    })
    .filter((sec): sec is NonNullable<typeof sec> => sec !== null)
  if (!effectiveText && !note && sections.length === 0) return null
  return {
    title: '',
    titleEn: '',
    effectiveText,
    ...(effectiveTextEn ? { effectiveTextEn } : {}),
    sections,
    ...(note ? { note } : {}),
    ...(noteEn ? { noteEn } : {}),
  }
}

/** 项目下拉选项（供「所属项目」绑定） */
interface ProjectOption {
  id: number
  name: string
}

/** 上传成功回填到 UploadFile.response 的形状：{ data: { url } } */
type PosterUploadResponse = { data?: { url?: string } }

const posterResponseUrl = (f: UploadFile): string | undefined =>
  (f.response as PosterUploadResponse | undefined)?.data?.url

/** 已存的 posterImages: string[] → 受控 fileList（重开弹窗可正确回填） */
const posterUrlsToFileList = (urls: string[] | null | undefined): UploadFile[] =>
  (Array.isArray(urls) ? urls : [])
    .filter((u): u is string => typeof u === 'string' && u.trim().length > 0)
    .map((url, i) => ({
      uid: `poster-${i}-${url}`,
      name: url.split('/').pop() || `poster_${i + 1}`,
      status: 'done' as const,
      url,
    }))

/** lib/request 缺 put 便捷方法（ST3 子表 upsert 用 PUT 语义），就地同形态封装（返回 response.data.data 层）。
 *  注意 .tsx 中泛型箭头函数须写 <T,> 否则 <T> 被解析为 JSX。 */
const put = <T,>(url: string, data?: unknown): Promise<T> =>
  request.put<{ code: number; data: T }>(url, data).then((res) => res.data.data)

/**
 * share_reward_by_reward_type_v1（ST3）：按 rewardTypeId 在落地页子表行数组中定位该类型那套。
 * 无行（新类型尚未配置）→ undefined，表单呈新建空白态，保存即 upsert 新建。
 */
const findShareRewardRow = (
  rows: ProjectPageShareReward[] | null | undefined,
  rewardTypeId: number | null | undefined,
): ProjectPageShareReward | undefined =>
  rewardTypeId == null ? undefined : (rows ?? []).find((r) => r.rewardTypeId === rewardTypeId)

/** 受控 fileList → 提交给后端的 posterImages: string[]（载荷格式保持不变） */
const fileListToPosterUrls = (files: UploadFile[]): string[] =>
  files
    .map((f) => posterResponseUrl(f) ?? f.url)
    .filter((u): u is string => typeof u === 'string' && u.length > 0)

/** 项目介绍落地页内容管理（小程序「项目介绍」及分享落地页数据源） */
const ProjectIntroPage: React.FC = () => {
  const role = useAuthStore((s) => s.user?.role)
  const canEdit = hasPermission(role, 'CONTENT_EDIT')
  // 海报大图上传/删除为招商部超管独占（后端 requirePermission('POSTER_UPLOAD') 同门禁）
  const canUploadPoster = hasPermission(role, 'POSTER_UPLOAD')

  const [list, setList] = useState<ProjectPageItem[]>([])
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState<ProjectPageItem | 'new' | null>(null)
  const [form] = Form.useForm<PageFormValue>()
  const [saving, setSaving] = useState(false)
  // 海报大图受控 fileList（不走 Form 字段，避免 destroyOnClose 丢状态）
  const [posterFileList, setPosterFileList] = useState<UploadFile[]>([])
  // landing_image_en_v1：英语版海报受控 fileList（同 posterFileList 模式）
  const [posterFileListEn, setPosterFileListEn] = useState<UploadFile[]>([])
  // liuwei_pages_v12：顶部横幅受控 fileList（同 posterFileList 模式，不走 Form 字段）
  const [bannerFileList, setBannerFileList] = useState<UploadFile[]>([])
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  // 所属项目下拉选项（multi_project_display_v1：落地页绑项目，首页卡片补 category/项目简介）
  const [projectOptions, setProjectOptions] = useState<ProjectOption[]>([])
  // share_reward_by_reward_type_v1（ST3）：「适用答谢类型」下拉数据源 + 当前选中类型
  const [rewardTypes, setRewardTypes] = useState<RewardType[]>([])
  const [shareRewardTypeId, setShareRewardTypeId] = useState<number | null>(null)

  const fetchList = useCallback(() => {
    setLoading(true)
    get<ProjectPageItem[]>('/admin/project-pages')
      .then((d) => setList(Array.isArray(d) ? d : []))
      .catch((err) => message.warning(errMsg(err, '项目介绍加载失败')))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { fetchList() }, [fetchList])

  // 拉取项目列表供「所属项目」下拉（仅打开编辑弹窗时需要，挂载一次即可）
  useEffect(() => {
    get<ProjectOption[]>('/admin/projects')
      .then((d) => setProjectOptions(Array.isArray(d) ? d.map((p) => ({ id: p.id, name: p.name })) : []))
      .catch(() => setProjectOptions([]))
  }, [])

  // ST3：拉取答谢类型列表供「适用答谢类型」下拉（BenefitConfig/index.tsx 同端点先例）；
  // 默认选中 isDefault 兜底类型（通常「标准」，以 DB 为准，不硬编码 code）。
  useEffect(() => {
    get<RewardType[]>('/admin/benefit-types')
      .then((d) => {
        const rows = Array.isArray(d) ? d : []
        setRewardTypes(rows)
        setShareRewardTypeId((prev) => prev ?? rows.find((t) => t.isDefault)?.id ?? rows[0]?.id ?? null)
      })
      .catch(() => setRewardTypes([]))
  }, [])

  /**
   * ST3：把「当前选中答谢类型」对应的子表行 content 回填进分享答谢方案表单；
   * 该类型尚无子表行 → 清空为新建态（保存即 upsert 新建，新类型天然多一套可编辑）。
   */
  const fillShareRewardForm = (rows: ProjectPageShareReward[] | null | undefined, typeId: number | null) => {
    const row = findShareRewardRow(rows, typeId)
    form.setFieldsValue(shareRewardToFormFields(row?.content ?? null))
  }

  /** ST3：切换「适用答谢类型」→ 重填分享答谢方案表单（当前编辑页的子表行数组） */
  const onShareRewardTypeChange = (typeId: number) => {
    setShareRewardTypeId(typeId)
    if (editing && editing !== 'new') {
      fillShareRewardForm(editing.shareRewards, typeId)
    } else {
      form.setFieldsValue(shareRewardToFormFields(null))
    }
  }

  const openEdit = (p?: ProjectPageItem) => {
    if (p) {
      setEditing(p)
      setPosterFileList(posterUrlsToFileList(p.posterImages))
      setPosterFileListEn(posterUrlsToFileList(p.posterImagesEn))
      setBannerFileList(posterUrlsToFileList(p.bannerUrls))
      form.setFieldsValue({
        title: p.title,
        titleEn: p.titleEn ?? undefined,
        projectId: p.projectId ?? null,
        intentCategory: p.intentCategory ?? null,
        coverUrl: p.coverUrl ?? undefined,
        shareImageUrl: p.shareImageUrl ?? undefined,
        coverUrlEn: p.coverUrlEn ?? undefined,
        shareImageUrlEn: p.shareImageUrlEn ?? undefined,
        longImageUrl: p.longImageUrl ?? undefined,
        longImageUrlEn: p.longImageUrlEn ?? undefined,
        cardDesc: p.cardDesc ?? undefined,
        cardDescEn: p.cardDescEn ?? undefined,
        sortOrder: p.sortOrder ?? 0,
      })
      // ST3：拉取该页「按答谢类型分套」子表行，回填到 p.shareRewards 后按当前选中类型填表单；
      // 子表接口未就绪/失败 → 回落旧单 JSON 字段 p.shareReward（旧数据仍可编辑）。
      get<ProjectPageShareReward[]>(`/admin/project-pages/${p.id}/share-rewards`)
        .then((rows) => {
          const arr = Array.isArray(rows) ? rows : []
          setEditing((prev) => (prev && prev !== 'new' && prev.id === p.id ? { ...prev, shareRewards: arr } : prev))
          fillShareRewardForm(arr, shareRewardTypeId)
        })
        .catch(() => {
          form.setFieldsValue(shareRewardToFormFields(p.shareReward))
        })
    } else {
      setEditing('new')
      setPosterFileList([])
      setPosterFileListEn([])
      setBannerFileList([])
      form.resetFields()
    }
  }

  const save = async () => {
    const v = await form.validateFields()
    // ST3：分享答谢方案不再随页面 PATCH 下发（避免与子表 upsert 双写）——
    // 单独组装后经 PUT /admin/project-pages/:id/share-rewards/:rewardTypeId 按类型 upsert。
    const shareRewardPayload = assembleShareRewardPayload(v)
    const payload = {
      title: v.title,
      titleEn: v.titleEn?.trim() || undefined,
      // 所属项目：null 表示解除关联；undefined 不传由后端忽略（此处恒显式传，允许解绑）
      projectId: v.projectId ?? null,
      // 意向业态（lead_intent_category_v1）：null 表示清空；用户从该落地页留资时小程序自动携带进线索
      intentCategory: v.intentCategory ?? null,
      coverUrl: v.coverUrl?.trim() || undefined,
      shareImageUrl: v.shareImageUrl?.trim() || undefined,
      // landing_image_en_v1：英语版（选填，空则英文模式回落中文版）
      coverUrlEn: v.coverUrlEn?.trim() || undefined,
      shareImageUrlEn: v.shareImageUrlEn?.trim() || undefined,
      longImageUrl: v.longImageUrl?.trim() || undefined,
      // long_image_en_v1：英语版长图（选填，空则英文模式回落中文版长图）
      longImageUrlEn: v.longImageUrlEn?.trim() || undefined,
      cardDesc: v.cardDesc?.trim() || undefined,
      cardDescEn: v.cardDescEn?.trim() || undefined,
      // 载荷格式与既有 PATCH 契约一致：string[] 图片 URL 列表
      posterImages: fileListToPosterUrls(posterFileList),
      posterImagesEn: fileListToPosterUrls(posterFileListEn),
      // liuwei_pages_v12：顶部横幅恒显式传（[] → 后端写 NULL，即清空）
      bannerUrls: fileListToPosterUrls(bannerFileList).slice(0, BANNER_MAX_COUNT),
      sortOrder: v.sortOrder ?? 0,
      // 长图化后不再编辑分段，恒传空数组清空（后端允许空数组；DB 字段保留向后兼容）
      sections: [],
    }
    setSaving(true)
    try {
      if (editing === 'new') {
        // 新建页：子表 upsert 依赖 pageId，须先保存落地页主体再配各类型答谢方案
        if (shareRewardPayload && shareRewardTypeId != null) {
          message.warning('请先保存落地页主体，再配置各类型答谢方案')
        }
        await post('/admin/project-pages', payload)
        message.success('落地页已新建（草稿状态）')
      } else if (editing) {
        await patch(`/admin/project-pages/${editing.id}`, payload)
        // ST3：分享答谢方案按当前选中类型走子表 upsert（PUT 幂等，同 page+type 仅一套）。
        // content 全空（null）时跳过 PUT——保留该类型既有配置，清空某类型需删除内容后由后端校验拦截提示。
        if (shareRewardPayload && shareRewardTypeId != null) {
          await put<ProjectPageShareReward>(
            `/admin/project-pages/${editing.id}/share-rewards/${shareRewardTypeId}`,
            { content: shareRewardPayload },
          )
        }
        message.success('落地页已更新')
      }
      setEditing(null)
      fetchList()
    } catch (err: unknown) {
      message.error(errMsg(err, '保存失败'))
    } finally {
      setSaving(false)
    }
  }

  const changeStatus = (r: ProjectPageItem, next: ProjectPageStatus) => {
    post(`/admin/project-pages/${r.id}/status`, { status: next })
      .then(() => {
        message.success(next === 'published' ? '已上架' : '已下架')
        fetchList()
      })
      .catch((err: unknown) => message.error(errMsg(err, '操作失败')))
  }

  /**
   * 上传前校验像素尺寸：不限比例（小程序画布按 1027×1365 拉伸填充，不裁切不变形），
   * 只拦过小（<640×480，拉伸后模糊）与过大（>4096）的图。
   * 注意 antd 的 Image 组件已占用 Image 标识符，此处须用 window.Image 取原生构造器。
   * objectURL 在成功/失败/解码异常三条路径上都必须释放。
   */
  const beforeUploadPoster: NonNullable<UploadProps['beforeUpload']> = async (file) => {
    const objectUrl = URL.createObjectURL(file)
    try {
      const size = await new Promise<{ w: number; h: number } | null>((resolve) => {
        const img = new window.Image()
        img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight })
        img.onerror = () => resolve(null)
        img.src = objectUrl
      })
      if (!size) {
        message.error('图片无法解析，请确认文件为有效的 JPG / PNG 图片')
        return Upload.LIST_IGNORE
      }
      const MIN_W = 640
      const MIN_H = 480
      const MAX_DIM = 4096
      if (size.w < MIN_W || size.h < MIN_H || size.w > MAX_DIM || size.h > MAX_DIM) {
        message.error(
          `图片尺寸须在 ${MIN_W}×${MIN_H} 到 ${MAX_DIM}×${MAX_DIM} 像素之间（不限比例，小程序端拉伸填充显示），当前为 ${size.w}×${size.h}`,
        )
        return Upload.LIST_IGNORE
      }
      return true
    } finally {
      URL.revokeObjectURL(objectUrl)
    }
  }

  /**
   * 自定义上传：multipart/form-data，字段名 file（后端 posterUpload.single('file')）。
   *
   * 必须显式把 Content-Type 置空：lib/request.ts 的 axios 实例默认带
   * 'Content-Type: application/json'，而 axios v1 的 transformRequest 在
   * hasJSONContentType 为真时会把 FormData 经 formDataToJSON 序列化成
   * {"file":{}} 再以 JSON 发出（实测确认），后端将收不到任何文件。
   * 置空后浏览器自动补 multipart 边界（boundary）。
   */
  const customRequestPoster: NonNullable<UploadProps['customRequest']> = async (options) => {
    const { file, onSuccess, onError } = options
    const formData = new FormData()
    formData.append('file', file as File)
    try {
      const data = await post<{ url: string }>(POSTER_UPLOAD_URL, formData, {
        headers: { 'Content-Type': undefined },
      })
      if (!data?.url) throw new Error('上传返回缺少 url')
      // 回填成 { data: { url } }，与 fileListToPosterUrls 的读取路径一致
      onSuccess?.({ data })
      message.success('海报大图已上传')
    } catch (err: unknown) {
      // 后端错误逐字透出：413「文件过大：单张不超过 10MB」/ 400「仅支持 JPG/PNG 格式的图片」
      message.error(errMsg(err, '上传失败'))
      onError?.(err instanceof Error ? err : new Error('上传失败'))
    }
  }

  /** 封面图上传：走通用图片上传端点 /admin/uploads/image（同已删除的 ReferredBenefits 页（thankyou_v1），
   *  成功后把返回的相对 URL 回填进 coverUrl 输入框。相对路径由小程序端补 BASE_URL 前缀。 */
  const customRequestCover: NonNullable<UploadProps['customRequest']> = async (options) => {
    const { file, onSuccess, onError } = options
    const formData = new FormData()
    formData.append('file', file as File)
    try {
      const data = await post<{ url: string }>('/admin/uploads/image', formData, {
        headers: { 'Content-Type': undefined },
      })
      if (!data?.url) throw new Error('上传返回缺少 url')
      form.setFieldValue('coverUrl', data.url)
      onSuccess?.({ data })
      message.success('封面图已上传')
    } catch (err: unknown) {
      message.error(errMsg(err, '上传失败'))
      onError?.(err instanceof Error ? err : new Error('上传失败'))
    }
  }

  /** 分享图上传：与 customRequestCover 同通道（/admin/uploads/image），成功后回填进 shareImageUrl 输入框。 */
  const customRequestShareImage: NonNullable<UploadProps['customRequest']> = async (options) => {
    const { file, onSuccess, onError } = options
    const formData = new FormData()
    formData.append('file', file as File)
    try {
      const data = await post<{ url: string }>('/admin/uploads/image', formData, {
        headers: { 'Content-Type': undefined },
      })
      if (!data?.url) throw new Error('上传返回缺少 url')
      form.setFieldValue('shareImageUrl', data.url)
      onSuccess?.({ data })
      message.success('分享图已上传')
    } catch (err: unknown) {
      message.error(errMsg(err, '上传失败'))
      onError?.(err instanceof Error ? err : new Error('上传失败'))
    }
  }

  /** 封面图（英语版）上传：与 customRequestCover 同通道（/admin/uploads/image），成功后回填进 coverUrlEn 输入框。 */
  const customRequestCoverEn: NonNullable<UploadProps['customRequest']> = async (options) => {
    const { file, onSuccess, onError } = options
    const formData = new FormData()
    formData.append('file', file as File)
    try {
      const data = await post<{ url: string }>('/admin/uploads/image', formData, {
        headers: { 'Content-Type': undefined },
      })
      if (!data?.url) throw new Error('上传返回缺少 url')
      form.setFieldValue('coverUrlEn', data.url)
      onSuccess?.({ data })
      message.success('封面图（英语版）已上传')
    } catch (err: unknown) {
      message.error(errMsg(err, '上传失败'))
      onError?.(err instanceof Error ? err : new Error('上传失败'))
    }
  }

  /** 分享图（英语版）上传：与 customRequestShareImage 同通道，成功后回填进 shareImageUrlEn 输入框。 */
  const customRequestShareImageEn: NonNullable<UploadProps['customRequest']> = async (options) => {
    const { file, onSuccess, onError } = options
    const formData = new FormData()
    formData.append('file', file as File)
    try {
      const data = await post<{ url: string }>('/admin/uploads/image', formData, {
        headers: { 'Content-Type': undefined },
      })
      if (!data?.url) throw new Error('上传返回缺少 url')
      form.setFieldValue('shareImageUrlEn', data.url)
      onSuccess?.({ data })
      message.success('分享图（英语版）已上传')
    } catch (err: unknown) {
      message.error(errMsg(err, '上传失败'))
      onError?.(err instanceof Error ? err : new Error('上传失败'))
    }
  }

  /** 海报大图（英语版）上传：与 customRequestPoster 同通道（POSTER_UPLOAD_URL），回填 { data: { url } }。 */
  const customRequestPosterEn: NonNullable<UploadProps['customRequest']> = async (options) => {
    const { file, onSuccess, onError } = options
    const formData = new FormData()
    formData.append('file', file as File)
    try {
      const data = await post<{ url: string }>(POSTER_UPLOAD_URL, formData, {
        headers: { 'Content-Type': undefined },
      })
      if (!data?.url) throw new Error('上传返回缺少 url')
      onSuccess?.({ data })
      message.success('海报大图（英语版）已上传')
    } catch (err: unknown) {
      message.error(errMsg(err, '上传失败'))
      onError?.(err instanceof Error ? err : new Error('上传失败'))
    }
  }

  /** 介绍长图上传：走通用图片上传端点（同 customRequestCover 的通道）。
   *  成功后把返回的相对 URL 回填进 longImageUrl 输入框。 */
  const customRequestLongImage: NonNullable<UploadProps['customRequest']> = async (options) => {
    const { file, onSuccess, onError } = options
    const formData = new FormData()
    formData.append('file', file as File)
    try {
      const data = await post<{ url: string }>('/admin/uploads/image', formData, {
        headers: { 'Content-Type': undefined },
      })
      if (!data?.url) throw new Error('上传返回缺少 url')
      form.setFieldValue('longImageUrl', data.url)
      onSuccess?.({ data })
      message.success('介绍长图已上传')
    } catch (err: unknown) {
      message.error(errMsg(err, '上传失败'))
      onError?.(err instanceof Error ? err : new Error('上传失败'))
    }
  }

  /** long_image_en_v1：介绍长图（英语版）上传：与 customRequestLongImage 同通道（/admin/uploads/image），成功后回填进 longImageUrlEn 输入框。 */
  const customRequestLongImageEn: NonNullable<UploadProps['customRequest']> = async (options) => {
    const { file, onSuccess, onError } = options
    const formData = new FormData()
    formData.append('file', file as File)
    try {
      const data = await post<{ url: string }>('/admin/uploads/image', formData, {
        headers: { 'Content-Type': undefined },
      })
      if (!data?.url) throw new Error('上传返回缺少 url')
      form.setFieldValue('longImageUrlEn', data.url)
      onSuccess?.({ data })
      message.success('介绍长图（英语版）已上传')
    } catch (err: unknown) {
      message.error(errMsg(err, '上传失败'))
      onError?.(err instanceof Error ? err : new Error('上传失败'))
    }
  }

  /**
   * liuwei_pages_v12：顶部横幅上传前校验——仅 JPG/PNG、单张 ≤ 500KB、总数 ≤ 5（多选时按批内序号累计）。
   * 不校验比例：小程序 aspectFill 居中裁切显示。
   */
  const beforeUploadBanner: NonNullable<UploadProps['beforeUpload']> = (file, batch) => {
    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      message.error('仅支持 JPG / PNG 图片')
      return Upload.LIST_IGNORE
    }
    if (file.size > BANNER_MAX_BYTES) {
      message.error(`单张横幅不超过 500KB，当前约 ${Math.ceil(file.size / 1024)}KB，请压缩后再上传`)
      return Upload.LIST_IGNORE
    }
    if (bannerFileList.length + batch.indexOf(file) >= BANNER_MAX_COUNT) {
      message.error(`顶部横幅最多 ${BANNER_MAX_COUNT} 张`)
      return Upload.LIST_IGNORE
    }
    return true
  }

  /** liuwei_pages_v12：顶部横幅上传——走通用图片端点 /admin/uploads/image（同介绍长图，CONTENT_EDIT 角色可用），回填 { data: { url } }。 */
  const customRequestBanner: NonNullable<UploadProps['customRequest']> = async (options) => {
    const { file, onSuccess, onError } = options
    const formData = new FormData()
    formData.append('file', file as File)
    try {
      const data = await post<{ url: string }>('/admin/uploads/image', formData, {
        headers: { 'Content-Type': undefined },
      })
      if (!data?.url) throw new Error('上传返回缺少 url')
      onSuccess?.({ data })
      message.success('横幅已上传')
    } catch (err: unknown) {
      message.error(errMsg(err, '上传失败'))
      onError?.(err instanceof Error ? err : new Error('上传失败'))
    }
  }

  const columns: ColumnsType<ProjectPageItem> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 64, render: (v: number) => `#${v}` },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (v: string) => <Text strong>{v}</Text>,
    },
    {
      title: '封面',
      dataIndex: 'coverUrl',
      key: 'coverUrl',
      width: 120,
      render: (v: string | null | undefined) =>
        v ? (
          <Image src={v} width={72} height={48} style={{ objectFit: 'cover', borderRadius: 6 }} />
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: '介绍长图',
      dataIndex: 'longImageUrl',
      key: 'longImageUrl',
      width: 100,
      render: (v: string | null | undefined) =>
        v ? (
          <Image src={v} width={40} height={72} style={{ objectFit: 'cover', borderRadius: 4 }} />
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    { title: '排序值', dataIndex: 'sortOrder', key: 'sortOrder', width: 80 },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 96,
      render: (v: ProjectPageStatus) => (
        <Tag color={STATUS_LABELS[v]?.color ?? 'default'}>{STATUS_LABELS[v]?.label ?? v}</Tag>
      ),
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 140,
      render: (v?: string) => fmtTime(v),
    },
    ...(canEdit
      ? [{
          title: '操作',
          key: 'actions',
          fixed: 'right' as const,
          width: 240,
          render: (_: unknown, r: ProjectPageItem) => (
            <Space size={4} wrap>
              <Button size="small" style={{ minHeight: 32 }} onClick={() => openEdit(r)}>编辑</Button>
              {r.status === 'published' ? (
                <Popconfirm
                  title="下架该落地页？"
                  description="下架后小程序端不再展示该内容"
                  okText="下架"
                  cancelText="取消"
                  onConfirm={() => changeStatus(r, 'offline')}
                >
                  <Button size="small" danger style={{ minHeight: 32 }}>下架</Button>
                </Popconfirm>
              ) : (
                <Popconfirm
                  title="上架该落地页？"
                  description="上架后小程序端立即可见"
                  okText="上架"
                  cancelText="取消"
                  onConfirm={() => changeStatus(r, 'published')}
                >
                  <Button size="small" type="primary" ghost style={{ minHeight: 32 }}>上架</Button>
                </Popconfirm>
              )}
              <Popconfirm
                title="删除该落地页？"
                description="删除后不可恢复，请确认"
                okText="删除"
                cancelText="取消"
                onConfirm={() =>
                  del(`/admin/project-pages/${r.id}`)
                    .then(() => { message.success('已删除'); fetchList() })
                    .catch((err: unknown) => message.error(errMsg(err, '删除失败')))
                }
              >
                <Button size="small" danger style={{ minHeight: 32 }}>删除</Button>
              </Popconfirm>
            </Space>
          ),
        }]
      : []),
  ]

  return (
    <>
      {/* 海报缩略卡锁 1027:1365 比例（设计稿 aspect-ratio:1027/1365），antd 默认为正方形 */}
      <style>{`
        .poster-upload-zone .ant-upload-list-picture-card .ant-upload-list-item-container,
        .poster-upload-zone .ant-upload.ant-upload-select {
          width: 100px;
          height: auto;
          aspect-ratio: ${POSTER_IMG_W} / ${POSTER_IMG_H};
        }
      `}</style>
      {/* 上传态缩略图点击放大（读态由 Image.PreviewGroup 自带） */}
      <Image
        wrapperStyle={{ display: 'none' }}
        src={previewUrl ?? undefined}
        preview={{
          visible: !!previewUrl,
          src: previewUrl ?? undefined,
          onVisibleChange: (v) => { if (!v) setPreviewUrl(null) },
        }}
      />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 14 }}
        message="项目基础信息（小程序介绍页顶部金字标题、白字副标题等）在「项目管理」标签按项目编辑；落地页、封面图、介绍长图、海报大图在「落地页内容」标签编辑。"
      />
      <Tabs
        defaultActiveKey="projects"
        items={[
          {
            key: 'projects',
            label: '项目管理',
            children: <ProjectsPage />,
          },
          {
            key: 'pages',
            label: '落地页内容',
            children: (
              <>
                <Space style={{ marginBottom: 14 }} wrap>
                  {canEdit && (
                    <Button type="primary" onClick={() => openEdit()} style={{ minHeight: 44 }}>
                      新建落地页
                    </Button>
                  )}
                </Space>
                <Table<ProjectPageItem>
                  rowKey="id"
                  loading={loading}
                  columns={columns}
                  dataSource={list}
                  scroll={{ x: 'max-content' }}
                  style={{ background: '#fff', borderRadius: 12 }}
                  pagination={{ pageSize: 20, showTotal: (t) => `共 ${t} 条` }}
                />
              </>
            ),
          },
        ]}
      />
      <Modal
        title={editing === 'new' ? '新建落地页' : '编辑落地页'}
        open={!!editing}
        onOk={save}
        onCancel={() => setEditing(null)}
        okText="保存"
        cancelText="取消"
        confirmLoading={saving}
        destroyOnClose
        width={720}
      >
        <Form<PageFormValue>
          form={form}
          layout="vertical"
          initialValues={{ sortOrder: 0 }}
        >
          <Form.Item
            name="title"
            label="标题（显示在小程序首页项目卡片右上、介绍页头图区与微信分享卡片）"
            rules={[
              { required: true, message: '标题必填' },
              { max: 100, message: '标题不超过 100 字' },
            ]}
          >
            <Input placeholder="例：德高信中央大道 · 千灯湖CBD甲级写字楼" maxLength={100} showCount />
          </Form.Item>
          <Form.Item name="titleEn" label="标题英文（选填 · 小程序英文模式显示）">
            <Input maxLength={100} placeholder="留空则英文模式仍显示中文" />
          </Form.Item>
          <Form.Item name="projectId" label="所属项目（选填 · 绑定后首页卡片显示项目类别标签，并用于留资意向项目归因）">
            <Select
              allowClear
              placeholder="不绑定项目（首页卡片仍显示，但无类别标签）"
              options={projectOptions.map((p) => ({ label: p.name, value: p.id }))}
            />
          </Form.Item>
          <Form.Item
            name="intentCategory"
            label="意向业态（必填 · 用户从本落地页留资时自动带进线索，留资页不再让用户自选）"
            rules={[{ required: true, message: '请选择意向业态' }]}
          >
            <Select
              placeholder="请选择意向业态"
              options={[
                { label: '写字楼', value: 'office' },
                { label: '租房', value: 'rental' },
                { label: '商业', value: 'retail' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="cardDesc"
            label="首页卡片简介（选填 · 显示在小程序首页项目卡片标题下方）"
            rules={[{ max: 500, message: '简介不超过 500 字' }]}
          >
            <Input.TextArea
              rows={2}
              maxLength={500}
              showCount
              placeholder="例：千灯湖CBD核心地段 · 甲级写字楼 · 地铁直达（留空则显示所属项目简介）"
            />
          </Form.Item>
          <Form.Item name="cardDescEn" label="首页卡片简介英文（选填 · 小程序英文模式显示）">
            <Input.TextArea
              rows={2}
              maxLength={500}
              placeholder="留空则英文模式仍显示中文"
            />
          </Form.Item>
          <Form.Item label="封面图（选填 · 首页项目卡片显示）">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <Form.Item name="coverUrl" noStyle>
                <Input style={{ flex: 1, minWidth: 240 }} placeholder="https://... 或上传后自动填入 /uploads/benefits/xxx" />
              </Form.Item>
              <Upload
                showUploadList={false}
                accept="image/jpeg,image/png"
                customRequest={customRequestCover}
                disabled={!canEdit}
              >
                <Button size="small" icon={<UploadOutlined />} disabled={!canEdit} style={{ minHeight: 32 }}>
                  上传封面
                </Button>
              </Upload>
            </div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>
              建议 500×400px（5:4）；不在这个比例微信会自动居中裁剪；支持 JPG / PNG，单张 ≤ 10MB
            </div>
          </Form.Item>
          {/* landing_image_en_v1：英语版封面图（选填，空则英文模式回落中文版封面） */}
          <Form.Item label="封面图（英语版 · 选填，空则用中文版）">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <Form.Item name="coverUrlEn" noStyle>
                <Input style={{ flex: 1, minWidth: 240 }} placeholder="https://... 或上传后自动填入 /uploads/benefits/xxx" />
              </Form.Item>
              <Upload
                showUploadList={false}
                accept="image/jpeg,image/png"
                customRequest={customRequestCoverEn}
                disabled={!canEdit}
              >
                <Button size="small" icon={<UploadOutlined />} disabled={!canEdit} style={{ minHeight: 32 }}>
                  上传封面（英语版）
                </Button>
              </Upload>
            </div>
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.coverUrlEn !== cur.coverUrlEn}>
              {({ getFieldValue }) => {
                const u = getFieldValue('coverUrlEn')
                return u ? (
                  <Image src={u} width={120} style={{ marginTop: 8, borderRadius: 6, display: 'block' }} />
                ) : null
              }}
            </Form.Item>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>
              小程序英文模式优先用本图，留空则显示中文版封面图；规格同中文版（建议 500×400px，JPG / PNG，单张 ≤ 10MB）
            </div>
          </Form.Item>
          <Form.Item label="分享图（选填 · 转发给好友时微信卡片显示）">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <Form.Item name="shareImageUrl" noStyle>
                <Input style={{ flex: 1, minWidth: 240 }} placeholder="https://... 或上传后自动填入 /uploads/benefits/xxx" />
              </Form.Item>
              <Upload
                showUploadList={false}
                accept="image/jpeg,image/png"
                customRequest={customRequestShareImage}
                disabled={!canEdit}
              >
                <Button size="small" icon={<UploadOutlined />} disabled={!canEdit} style={{ minHeight: 32 }}>
                  上传分享图
                </Button>
              </Upload>
            </div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>
              建议 500×400px（5:4），与封面图同比例；支持 JPG / PNG，单张 ≤ 10MB；不传则分享时仍用封面图
            </div>
          </Form.Item>
          {/* landing_image_en_v1：英语版分享图（选填，空则英文模式回落中文版分享图→封面链） */}
          <Form.Item label="分享图（英语版 · 选填，空则用中文版）">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <Form.Item name="shareImageUrlEn" noStyle>
                <Input style={{ flex: 1, minWidth: 240 }} placeholder="https://... 或上传后自动填入 /uploads/benefits/xxx" />
              </Form.Item>
              <Upload
                showUploadList={false}
                accept="image/jpeg,image/png"
                customRequest={customRequestShareImageEn}
                disabled={!canEdit}
              >
                <Button size="small" icon={<UploadOutlined />} disabled={!canEdit} style={{ minHeight: 32 }}>
                  上传分享图（英语版）
                </Button>
              </Upload>
            </div>
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.shareImageUrlEn !== cur.shareImageUrlEn}>
              {({ getFieldValue }) => {
                const u = getFieldValue('shareImageUrlEn')
                return u ? (
                  <Image src={u} width={120} style={{ marginTop: 8, borderRadius: 6, display: 'block' }} />
                ) : null
              }}
            </Form.Item>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>
              小程序英文模式分享优先用本图，留空则按「中文版分享图 → 英语版封面图 → 中文版封面图」回落；规格同中文版
            </div>
          </Form.Item>
          {/* share_reward_rule_end_v1：分享答谢方案编辑区（嵌套 Form.List 增删区块/小卡片）。
              大标题固定走小程序 i18n「本期答谢方案」，此处不出输入框（供图注释口径）。 */}
          <Form.Item label="分享答谢方案（选填 · 小程序分享弹窗展示，中英文分开填写）">
            <div
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                padding: 16,
                background: '#fafafa',
              }}
            >
              <Form.Item label="适用答谢类型（share_reward_by_reward_type_v1：每个类型各配一套弹窗，小程序按推荐人类型显示对应那套）" style={{ marginBottom: 12 }}>
                <Select
                  value={shareRewardTypeId ?? undefined}
                  onChange={onShareRewardTypeChange}
                  placeholder="选择答谢类型"
                  options={rewardTypes.map((t) => ({
                    label: `${t.name}${t.isDefault ? '（默认/兜底）' : ''}`,
                    value: t.id,
                  }))}
                />
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>
                  切换类型即编辑该类型那套内容；尚无配置的类型显示空白表单，保存即为该类型新建一套；新增答谢类型（答谢配置页）后此处自动多一套可编辑
                </div>
              </Form.Item>
              <Form.Item
                name="shareRewardEffectiveText"
                label="有效期文字"
                style={{ marginBottom: 12 }}
                rules={[{ max: 50, message: '不超过 50 字' }]}
              >
                <Input placeholder="如「有效至 2026-12-31」" maxLength={50} showCount />
              </Form.Item>
              <Form.Item
                name="shareRewardEffectiveTextEn"
                label="有效期文字（英文）"
                style={{ marginBottom: 12 }}
                rules={[{ max: 50, message: '不超过 50 字' }]}
              >
                <Input placeholder="留空则英文模式仍显示中文" maxLength={50} />
              </Form.Item>

              <Form.List name="shareRewardSections">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field) => (
                      <div
                        key={field.key}
                        style={{
                          border: '1px solid #d9d9d9',
                          borderRadius: 8,
                          padding: 12,
                          marginBottom: 12,
                          background: '#fff',
                        }}
                      >
                        <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }} align="baseline">
                          <Text strong style={{ fontSize: 13 }}>区块 {field.name + 1}</Text>
                          <Button size="small" danger onClick={() => remove(field.name)} style={{ minHeight: 32 }}>
                            删除区块
                          </Button>
                        </Space>
                        <Form.Item
                          name={[field.name, 'heading']}
                          label="区块标题"
                          rules={[
                            { required: true, message: '区块标题必填' },
                            { max: 50, message: '不超过 50 字' },
                          ]}
                          style={{ marginBottom: 12 }}
                        >
                          <Input placeholder="如 我的答谢 / 客户入驻礼遇 / 本渠道说明" maxLength={50} />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, 'headingEn']}
                          label="区块标题（英文）"
                          rules={[{ max: 50, message: '不超过 50 字' }]}
                          style={{ marginBottom: 12 }}
                        >
                          <Input placeholder="留空则英文模式仍显示中文" maxLength={50} />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, 'body']}
                          label="区块正文"
                          rules={[
                            { required: true, message: '区块正文必填' },
                            { max: 1000, message: '不超过 1000 字' },
                          ]}
                          style={{ marginBottom: 12 }}
                        >
                          <Input.TextArea autoSize={{ minRows: 3 }} maxLength={1000} showCount placeholder="支持多行，小程序弹窗按换行展示" />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, 'bodyEn']}
                          label="区块正文（英文）"
                          rules={[{ max: 1000, message: '不超过 1000 字' }]}
                          style={{ marginBottom: 12 }}
                        >
                          <Input.TextArea autoSize={{ minRows: 3 }} maxLength={1000} placeholder="留空则英文模式仍显示中文" />
                        </Form.Item>
                        {/* 小卡片（chips，0..4 个两栏展示） */}
                        <Form.List name={[field.name, 'chips']}>
                          {(chipFields, { add: addChip, remove: removeChip }) => (
                            <div
                              style={{
                                borderTop: '1px dashed #e5e7eb',
                                paddingTop: 8,
                              }}
                            >
                              {chipFields.map((chipField) => (
                                <div
                                  key={chipField.key}
                                  style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    columnGap: 8,
                                    rowGap: 4,
                                    marginBottom: 8,
                                    padding: 8,
                                    border: '1px solid #f0f0f0',
                                    borderRadius: 6,
                                  }}
                                >
                                  <Form.Item
                                    name={[chipField.name, 'label']}
                                    label="标签"
                                    rules={[
                                      { required: true, message: '必填' },
                                      { max: 20, message: '不超过 20 字' },
                                    ]}
                                    style={{ marginBottom: 4 }}
                                  >
                                    <Input placeholder="如 税前标准 / < 400㎡" maxLength={20} />
                                  </Form.Item>
                                  <Form.Item
                                    name={[chipField.name, 'labelEn']}
                                    label="标签（英文）"
                                    rules={[{ max: 40, message: '不超过 40 字' }]}
                                    style={{ marginBottom: 4 }}
                                  >
                                    <Input placeholder="选填" maxLength={40} />
                                  </Form.Item>
                                  <Form.Item
                                    name={[chipField.name, 'value']}
                                    label="数值"
                                    rules={[
                                      { required: true, message: '必填' },
                                      { max: 100, message: '不超过 100 字' },
                                    ]}
                                    style={{ marginBottom: 4 }}
                                  >
                                    <Input placeholder="如 首月租金 / 1 万元" maxLength={100} />
                                  </Form.Item>
                                  <Form.Item
                                    name={[chipField.name, 'valueEn']}
                                    label="数值（英文）"
                                    rules={[{ max: 100, message: '不超过 100 字' }]}
                                    style={{ marginBottom: 4 }}
                                  >
                                    <Input placeholder="选填" maxLength={100} />
                                  </Form.Item>
                                  <div style={{ gridColumn: '1 / -1', textAlign: 'right' }}>
                                    <Button size="small" danger onClick={() => removeChip(chipField.name)} style={{ minHeight: 32 }}>
                                      删除小卡片
                                    </Button>
                                  </div>
                                </div>
                              ))}
                              <Button
                                size="small"
                                type="dashed"
                                disabled={chipFields.length >= SHARE_REWARD_CHIP_MAX}
                                onClick={() => addChip()}
                                style={{ minHeight: 32 }}
                              >
                                ＋ 添加小卡片
                              </Button>
                              <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                                最多 {SHARE_REWARD_CHIP_MAX} 个
                              </Text>
                            </div>
                          )}
                        </Form.List>
                      </div>
                    ))}
                    <Button
                      type="dashed"
                      block
                      disabled={fields.length >= SHARE_REWARD_SECTION_MAX}
                      onClick={() => add()}
                      style={{ minHeight: 36 }}
                    >
                      ＋ 添加区块
                    </Button>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>
                      最多 {SHARE_REWARD_SECTION_MAX} 个区块；区块内的「小卡片」为选填的两栏展示项
                    </div>
                  </>
                )}
              </Form.List>

              <Form.Item
                name="shareRewardNote"
                label="底部注释"
                style={{ marginTop: 16, marginBottom: 12 }}
                rules={[{ max: 300, message: '不超过 300 字' }]}
              >
                <Input.TextArea rows={2} maxLength={300} showCount placeholder="如 以您提交推荐时适用的方案为准，方案调整不影响调整前已提交的推荐" />
              </Form.Item>
              <Form.Item
                name="shareRewardNoteEn"
                label="底部注释（英文）"
                style={{ marginBottom: 0 }}
                rules={[{ max: 300, message: '不超过 300 字' }]}
              >
                <Input.TextArea rows={2} maxLength={300} placeholder="留空则英文模式仍显示中文" />
              </Form.Item>
            </div>
          </Form.Item>
          {/* liuwei_pages_v12：项目介绍页顶部轮播横幅（最多 5 张，7:3） */}
          <Form.Item label="顶部横幅（选填 · 项目介绍页顶部轮播）">
            <div className="poster-upload-zone">
              <Upload
                listType="picture-card"
                multiple
                accept="image/jpeg,image/png"
                fileList={bannerFileList}
                disabled={!canEdit}
                beforeUpload={beforeUploadBanner}
                customRequest={customRequestBanner}
                onPreview={(f) => setPreviewUrl(posterResponseUrl(f) ?? f.url ?? null)}
                onChange={({ fileList }) =>
                  // 失败项不入列（已 message.error 提示），且截断到上限，避免误存空 URL / 超量
                  setBannerFileList(fileList.filter((f) => f.status !== 'error').slice(0, BANNER_MAX_COUNT))
                }
              >
                {canEdit && bannerFileList.length < BANNER_MAX_COUNT ? (
                  <div style={{ color: '#6b7280' }}>
                    <div style={{ fontSize: 20, lineHeight: 1 }}>＋</div>
                    <div style={{ fontSize: 12, marginTop: 2 }}>上传</div>
                  </div>
                ) : null}
              </Upload>
              <div style={{ fontSize: 12, color: '#9ca3af', margin: '6px 0 0' }}>{BANNER_HINT}</div>
            </div>
          </Form.Item>
          <Form.Item label="介绍长图（选填 · 小程序介绍页全宽铺满展示）">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <Form.Item name="longImageUrl" noStyle>
                <Input style={{ flex: 1, minWidth: 240 }} placeholder="https://... 或上传后自动填入 /uploads/benefits/xxx" />
              </Form.Item>
              <Upload
                showUploadList={false}
                accept="image/jpeg,image/png"
                customRequest={customRequestLongImage}
                disabled={!canEdit}
              >
                <Button size="small" icon={<UploadOutlined />} disabled={!canEdit} style={{ minHeight: 32 }}>
                  上传长图
                </Button>
              </Upload>
            </div>
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.longImageUrl !== cur.longImageUrl}>
              {({ getFieldValue }) => {
                const u = getFieldValue('longImageUrl')
                return u ? (
                  <Image src={u} width={120} style={{ marginTop: 8, borderRadius: 6, display: 'block' }} />
                ) : null
              }}
            </Form.Item>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>
              建议宽度 750px 以上、高度不限的一整张长图；小程序介绍页按宽度铺满、高度随图自适应；支持 JPG / PNG，单张 ≤ 10MB
            </div>
          </Form.Item>
          {/* long_image_en_v1：介绍长图（英语版 · 选填，空则英文模式回落中文版长图） */}
          <Form.Item label="介绍长图（英语版 · 选填，空则用中文版）">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <Form.Item name="longImageUrlEn" noStyle>
                <Input style={{ flex: 1, minWidth: 240 }} placeholder="https://... 或上传后自动填入 /uploads/benefits/xxx" />
              </Form.Item>
              <Upload
                showUploadList={false}
                accept="image/jpeg,image/png"
                customRequest={customRequestLongImageEn}
                disabled={!canEdit}
              >
                <Button size="small" icon={<UploadOutlined />} disabled={!canEdit} style={{ minHeight: 32 }}>
                  上传长图（英语版）
                </Button>
              </Upload>
            </div>
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.longImageUrlEn !== cur.longImageUrlEn}>
              {({ getFieldValue }) => {
                const u = getFieldValue('longImageUrlEn')
                return u ? (
                  <Image src={u} width={120} style={{ marginTop: 8, borderRadius: 6, display: 'block' }} />
                ) : null
              }}
            </Form.Item>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>
              小程序英文模式优先用本图，留空则显示中文版长图；规格同中文版（建议宽度 750px 以上，JPG / PNG，单张 ≤ 10MB）
            </div>
          </Form.Item>
          <Form.Item label="海报大图（全项目共享 · 推荐人生成海报时可选其一作为顶部大图；二维码按项目区分）">
            {canUploadPoster ? (
              <div className="poster-upload-zone">
                <Upload
                  listType="picture-card"
                  multiple
                  accept="image/jpeg,image/png"
                  fileList={posterFileList}
                  beforeUpload={beforeUploadPoster}
                  customRequest={customRequestPoster}
                  onPreview={(f) => setPreviewUrl(posterResponseUrl(f) ?? f.url ?? null)}
                  onChange={({ fileList }) =>
                    // 失败项不入列（已用 message.error 提示），避免误存为空 URL
                    setPosterFileList(fileList.filter((f) => f.status !== 'error'))
                  }
                >
                  <div style={{ color: '#6b7280' }}>
                    <div style={{ fontSize: 20, lineHeight: 1 }}>＋</div>
                    <div style={{ fontSize: 12, marginTop: 2 }}>上传</div>
                  </div>
                </Upload>
                <div style={{ fontSize: 12, color: '#9ca3af', margin: '6px 0 0' }}>{POSTER_HINT}</div>
              </div>
            ) : (
              <>
                {posterFileList.length > 0 ? (
                  <Image.PreviewGroup>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
                        gap: 10,
                      }}
                    >
                      {posterFileList.map((f) => (
                        <Image
                          key={f.uid}
                          src={posterResponseUrl(f) ?? f.url}
                          style={{
                            width: '100%',
                            aspectRatio: `${POSTER_IMG_W} / ${POSTER_IMG_H}`,
                            objectFit: 'cover',
                            borderRadius: 6,
                            background: '#e8eaed',
                          }}
                        />
                      ))}
                    </div>
                  </Image.PreviewGroup>
                ) : (
                  <Text type="secondary">暂无海报大图</Text>
                )}
                <div
                  style={{
                    marginTop: 9,
                    background: '#f3f4f6',
                    color: '#6b7280',
                    fontSize: 12,
                    padding: '7px 10px',
                    borderRadius: 5,
                  }}
                >
                  {POSTER_READONLY_HINT}
                </div>
              </>
            )}
          </Form.Item>
          {/* landing_image_en_v1：英语版海报大图（选填，空则英文模式回落中文版海报） */}
          <Form.Item label="海报大图（英语版 · 选填，空则用中文版）">
            {canUploadPoster ? (
              <div className="poster-upload-zone">
                <Upload
                  listType="picture-card"
                  multiple
                  accept="image/jpeg,image/png"
                  fileList={posterFileListEn}
                  beforeUpload={beforeUploadPoster}
                  customRequest={customRequestPosterEn}
                  onPreview={(f) => setPreviewUrl(posterResponseUrl(f) ?? f.url ?? null)}
                  onChange={({ fileList }) =>
                    setPosterFileListEn(fileList.filter((f) => f.status !== 'error'))
                  }
                >
                  <div style={{ color: '#6b7280' }}>
                    <div style={{ fontSize: 20, lineHeight: 1 }}>＋</div>
                    <div style={{ fontSize: 12, marginTop: 2 }}>上传</div>
                  </div>
                </Upload>
                <div style={{ fontSize: 12, color: '#9ca3af', margin: '6px 0 0' }}>
                  小程序英文模式优先用本组海报，留空则显示中文版海报；{POSTER_HINT}
                </div>
              </div>
            ) : (
              <>
                {posterFileListEn.length > 0 ? (
                  <Image.PreviewGroup>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
                        gap: 10,
                      }}
                    >
                      {posterFileListEn.map((f) => (
                        <Image
                          key={f.uid}
                          src={posterResponseUrl(f) ?? f.url}
                          style={{
                            width: '100%',
                            aspectRatio: `${POSTER_IMG_W} / ${POSTER_IMG_H}`,
                            objectFit: 'cover',
                            borderRadius: 6,
                            background: '#e8eaed',
                          }}
                        />
                      ))}
                    </div>
                  </Image.PreviewGroup>
                ) : (
                  <Text type="secondary">暂无英语版海报大图</Text>
                )}
                <div
                  style={{
                    marginTop: 9,
                    background: '#f3f4f6',
                    color: '#6b7280',
                    fontSize: 12,
                    padding: '7px 10px',
                    borderRadius: 5,
                  }}
                >
                  {POSTER_READONLY_HINT}
                </div>
              </>
            )}
          </Form.Item>
          <Form.Item name="sortOrder" label="排序值（越小越靠前）">
            <InputNumber min={0} style={{ width: '100%', minHeight: 44 }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default ProjectIntroPage
